/**
 * iter-204 — What-if Scenarios.
 *
 * Replaces the iter-170x stub with the live calculator. Single
 * scenario per user. Renders:
 *   - KPI strip (final cash, lowest week, runway months)
 *   - Line chart baseline vs scenario over 13 weeks
 *   - Mod list with archetype-specific preset chips
 *   - Add / Remove mods inline
 *
 * Respects the existing feature gate (LockedPage) for non-eligible plans.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell, CheckCircle2, Hourglass, MinusCircle, PlusCircle, Plus, RefreshCw,
  Trash2, TrendingDown, TrendingUp, Wallet, X, Sparkles,
} from "lucide-react";
import * as Lucide from "lucide-react";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";

import { Layout } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { IconToggle } from "../../components/ui/icon-toggle";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { PageHeader } from "../../components/PageHeader";
import { LockedPage } from "../../components/LockedPage";
import { useLang } from "../../contexts/LangContext";
import { useGatedAction } from "../../hooks/useGatedAction";
import { api } from "../../lib/api";
import { tx } from "../../i18n";
import { toast } from "sonner";

const MOD_ICON = {
  lose_recurring: MinusCircle,
  delay_receivable: Hourglass,
  add_expense: TrendingDown,
  add_income: PlusCircle,
};

const fmtMoney = (n, ccy) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency: ccy || "USD", maximumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return `${ccy || ""} ${Math.round(Number(n)).toLocaleString()}`;
  }
};

function PresetChip({ preset, lang, onPick, disabled }) {
  const Icon = Lucide[preset.icon] || Sparkles;
  const label = preset[`label_${lang === "en" ? "en" : lang === "pt" ? "pt" : "es"}`] || preset.label_es;
  return (
    <button /* design-system: ALLOW legacy-todo */
      type="button"
      onClick={() => onPick(preset)}
      disabled={disabled}
      data-testid={`scenarios-preset-${preset.type}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 hover:border-sky-300 hover:bg-sky-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Icon className="h-3.5 w-3.5 text-sky-600" />
      {label}
    </button>
  );
}

function ModEditor({ mod, anchors, onChange, onRemove, lang, profileCcy }) {
  const Icon = MOD_ICON[mod.type] || PlusCircle;
  const isReceivableMod = mod.type === "lose_recurring" || mod.type === "delay_receivable";
  const filteredAnchors = mod.type === "lose_recurring"
    ? anchors.filter((a) => a.is_recurring)
    : anchors;
  const ccy = (mod.ccy || profileCcy || "USD").toUpperCase();

  return (
    <Card className="p-4 rounded-2xl border-slate-200" data-testid={`scenarios-mod-${mod.id}`}>
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-full bg-sky-100 text-sky-700 inline-flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0 space-y-2">
          <Input
            data-testid={`scenarios-mod-${mod.id}-label`}
            placeholder={tx(lang, "Mod label", "Etiqueta del cambio", "Rótulo")}
            value={mod.label || ""}
            onChange={(e) => onChange({ ...mod, label: e.target.value })}
            className="font-semibold"
          />

          {isReceivableMod && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">
                {tx(lang, "Receivable", "Cobro", "Cobrança")}
              </Label>
              <Select
                value={mod.receivable_id || ""}
                onValueChange={(v) => onChange({ ...mod, receivable_id: v })}
                disabled={filteredAnchors.length === 0}
              >
                <SelectTrigger data-testid={`scenarios-mod-${mod.id}-receivable`}>
                  <SelectValue placeholder={
                    filteredAnchors.length === 0
                      ? tx(lang, "No receivables available", "Sin cobros disponibles", "Sem cobranças disponíveis")
                      : tx(lang, "Pick a receivable…", "Selecciona un cobro…", "Selecione…")
                  } />
                </SelectTrigger>
                <SelectContent>
                  {filteredAnchors.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500" data-testid={`scenarios-mod-${mod.id}-no-anchors`}>
                      {tx(lang, "No receivables available", "Sin cobros disponibles", "Sem cobranças disponíveis")}
                    </div>
                  )}
                  {filteredAnchors.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label} · {fmtMoney(a.amount, a.currency)} · {(a.expected_date || "").slice(0, 10)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filteredAnchors.length === 0 && (
                <p className="text-[11px] text-slate-500 mt-1" data-testid={`scenarios-mod-${mod.id}-no-anchors-hint`}>
                  {mod.type === "lose_recurring"
                    ? tx(lang,
                        "Add a recurring receivable first to use this mod.",
                        "Crea un cobro recurrente para usar este cambio.",
                        "Crie uma cobrança recorrente para usar.")
                    : tx(lang,
                        "Add a pending receivable first.",
                        "Crea un cobro pendiente primero.",
                        "Crie uma cobrança pendente primeiro.")}
                </p>
              )}
            </div>
          )}

          {mod.type === "delay_receivable" && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">{tx(lang, "Days late", "Días de atraso", "Dias de atraso")}</Label>
              <Input
                type="number"
                min="1"
                data-testid={`scenarios-mod-${mod.id}-days`}
                value={mod.days || ""}
                onChange={(e) => onChange({ ...mod, days: parseInt(e.target.value || "0", 10) })}
              />
            </div>
          )}

          {mod.type === "lose_recurring" && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">{tx(lang, "Loss starts on", "Pérdida desde", "Perda a partir de")}</Label>
              <Input
                type="date"
                data-testid={`scenarios-mod-${mod.id}-from-date`}
                value={mod.from_date || ""}
                onChange={(e) => onChange({ ...mod, from_date: e.target.value })}
              />
            </div>
          )}

          {(mod.type === "add_expense" || mod.type === "add_income") && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-slate-500">{tx(lang, "Amount", "Monto", "Valor")}</Label>
                <Input
                  type="number"
                  data-testid={`scenarios-mod-${mod.id}-amount`}
                  value={mod.amount ?? ""}
                  onChange={(e) => onChange({ ...mod, amount: parseFloat(e.target.value || "0") })}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">{tx(lang, "Currency", "Moneda", "Moeda")}</Label>
                <Select
                  value={ccy}
                  onValueChange={(v) => onChange({ ...mod, ccy: v })}
                >
                  <SelectTrigger data-testid={`scenarios-mod-${mod.id}-ccy`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["USD", "EUR", "COP", "MXN", "BRL", "ARS", "CLP", "PEN", "GBP"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">{tx(lang, "Date", "Fecha", "Data")}</Label>
                <Input
                  type="date"
                  data-testid={`scenarios-mod-${mod.id}-date`}
                  value={mod.date || ""}
                  onChange={(e) => onChange({ ...mod, date: e.target.value })}
                />
              </div>
              {mod.type === "add_income" && (
                <div className="col-span-3">
                  <Label className="text-xs text-slate-500">{tx(lang, "Retention %", "Retención %", "Retenção %")}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    data-testid={`scenarios-mod-${mod.id}-retention`}
                    value={mod.retention_pct ?? ""}
                    onChange={(e) => onChange({ ...mod, retention_pct: parseFloat(e.target.value || "0") })}
                  />
                </div>
              )}
              {ccy !== (profileCcy || "USD").toUpperCase() && (
                <p className="col-span-3 text-[11px] text-slate-500" data-testid={`scenarios-mod-${mod.id}-fx-note`}>
                  {tx(lang,
                    `Converted to ${profileCcy} at compute time.`,
                    `Se convierte a ${profileCcy} al calcular.`,
                    `Convertido para ${profileCcy} no cálculo.`)}
                </p>
              )}
            </div>
          )}
        </div>
        <IconToggle
          onClick={() => onRemove(mod.id)}
          aria-label="Remove mod"
          data-testid={`scenarios-mod-${mod.id}-remove`}
          variant="danger"
          size="lg"
          className="shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </IconToggle>
      </div>
    </Card>
  );
}

function KpiCard({ icon: Icon, label, value, sublabel, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-700",
    rose: "bg-rose-100 text-rose-700",
    violet: "bg-sky-100 text-sky-700",
  };
  return (
    <Card className="p-4 rounded-2xl border-slate-200" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className={`h-8 w-8 rounded-full inline-flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">{label}</p>
      </div>
      <p className="text-xl font-display font-bold text-slate-900 mt-2">{value}</p>
      {sublabel && <p className="text-[11px] text-slate-500 mt-0.5">{sublabel}</p>}
    </Card>
  );
}

// Pure helper outside the component so the React compiler doesn't
// mark these (non-deterministic) calls as a memoization hazard.
const _newModId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID().slice(0, 12) : `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function _makeModFromPreset(preset, lang) {
  const nowMs = Date.now();
  const future = (offset) => new Date(nowMs + (offset || 0) * 86400000).toISOString().slice(0, 10);
  const mod = {
    id: _newModId(),
    type: preset.type,
    label: preset[`label_${lang === "en" ? "en" : lang === "pt" ? "pt" : "es"}`] || preset.label_es,
    archetype_template: preset.type,
  };
  if (preset.amount != null) mod.amount = preset.amount;
  if (preset.days_from_now != null) mod.date = future(preset.days_from_now);
  if (preset.days != null) mod.days = preset.days;
  if (preset.retention_pct != null) mod.retention_pct = preset.retention_pct;
  if (preset.type === "lose_recurring") mod.from_date = future(0);
  return mod;
}

export default function Scenarios() {
  const { lang } = useLang();
  const gate = useGatedAction("scenarios");

  const [scenario, setScenario] = useState(null);
  const [presets, setPresets] = useState([]);
  const [quickBundle, setQuickBundle] = useState(null);
  const [anchors, setAnchors] = useState([]);
  const [projection, setProjection] = useState(null);
  const [saving, setSaving] = useState(false);
  const fetchedRef = useRef(false);

  const loadAll = useCallback(async () => {
    try {
      const [sc, pr, qs, an] = await Promise.all([
        api.get("/scenarios"),
        api.get("/scenarios/presets"),
        api.get("/scenarios/quick-scenario"),
        api.get("/scenarios/anchors"),
      ]);
      setScenario(sc.data);
      setPresets(pr.data?.presets || []);
      setQuickBundle(qs.data?.bundle || null);
      setAnchors(an.data?.receivables || []);
      const cp = await api.get("/scenarios/compute");
      setProjection(cp.data);
    } catch (e) {
      // gate.locked path renders LockedPage; everything else is a soft
      // failure — we surface a toast and let the user retry.
      toast.error(tx(lang, "Couldn't load scenarios.", "No pudimos cargar escenarios.", "Não foi possível carregar."));
    }
  }, [lang]);

  useEffect(() => {
    if (gate.locked || fetchedRef.current) return;
    fetchedRef.current = true;
    void loadAll();
  }, [gate.locked, loadAll]);

  const save = useCallback(async (nextMods, nextName) => {
    setSaving(true);
    try {
      await api.put("/scenarios", {
        name: nextName ?? scenario?.name ?? "Mi escenario",
        mods: nextMods,
      });
      setScenario((cur) => ({ ...(cur || {}), mods: nextMods, name: nextName ?? cur?.name }));
      const cp = await api.get("/scenarios/compute");
      setProjection(cp.data);
    } catch (e) {
      toast.error(tx(lang, "Save failed.", "No se pudo guardar.", "Falha ao salvar."));
    } finally {
      setSaving(false);
    }
  }, [scenario?.name, lang]);

  const handlePreset = (preset) => {
    void save([...(scenario?.mods || []), _makeModFromPreset(preset, lang)]);
  };

  // iter-210 — apply the full archetype "stress test" bundle in one click.
  // Replaces the existing mods (intentional: this is a *scenario*, not
  // additive — so the resulting projection is the bundle's narrative).
  const handleQuickScenario = () => {
    if (!quickBundle?.mods?.length) return;
    const nextMods = quickBundle.mods.map((p) => _makeModFromPreset(p, lang));
    const nextName = quickBundle[`title_${lang === "en" ? "en" : lang === "pt" ? "pt" : "es"}`]
      || quickBundle.title_es;
    void save(nextMods, nextName);
    toast.success(tx(lang,
      "Quick scenario applied.",
      "Escenario rápido aplicado.",
      "Cenário rápido aplicado.",
    ));
  };

  function updateMod(next) {
    const nextMods = (scenario?.mods || []).map((m) => (m.id === next.id ? next : m));
    void save(nextMods);
  }

  function removeMod(id) {
    const nextMods = (scenario?.mods || []).filter((m) => m.id !== id);
    void save(nextMods);
  }

  async function reset() {
    await api.delete("/scenarios");
    setScenario({ name: "Mi escenario", mods: [], exists: false });
    const cp = await api.get("/scenarios/compute");
    setProjection(cp.data);
    toast.success(tx(lang, "Scenario reset.", "Escenario reiniciado.", "Cenário reiniciado."));
  }

  const currency = projection?.currency || "USD";

  const chartData = useMemo(
    () => (projection?.chart || []).map((p) => ({ ...p, weekLabel: `S${p.week}` })),
    [projection?.chart],
  );

  if (gate.locked) {
    return (
      <LockedPage
        featureKey="scenarios"
        titleEs="Escenarios what-if"
        titleEn="What-if Scenarios"
        titlePt="Cenários what-if"
        bullets={[
          { es: "Modela la pérdida de un retainer y mira el impacto a 90 días.", en: "Model losing a retainer and see the 90-day impact.", pt: "Modele a perda de um retainer e veja o impacto de 90 dias." },
          { es: "Simula un proyecto spot con retención fiscal.", en: "Simulate a spot project with tax withholding.", pt: "Simule um projeto spot com retenção fiscal." },
          { es: "Calcula el atraso de un cobro de N días.", en: "Compute the cash delta of an N-day receivable delay.", pt: "Calcule o atraso de um cobro de N dias." },
        ]}
      />
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl" data-testid="scenarios-page">
        <PageHeader
          title={tx(lang, "What-if Scenarios", "Escenarios what-if", "Cenários what-if")}
          subtitle={tx(lang,
            "Stress-test the next 13 weeks against changes in your income or expenses.",
            "Pon a prueba las próximas 13 semanas contra cambios en ingresos o gastos.",
            "Teste as próximas 13 semanas contra mudanças em receitas ou gastos.")}
        />

        {/* KPI strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard
            testid="scenarios-kpi-final-cash"
            icon={Wallet}
            label={tx(lang, "Final cash · S13", "Caja al final · S13", "Caixa final · S13")}
            value={fmtMoney(projection?.kpis_scenario?.final_cash, currency)}
            sublabel={projection ? `${tx(lang, "Baseline", "Sin escenario", "Sem cenário")}: ${fmtMoney(projection.kpis_baseline?.final_cash, currency)}` : ""}
            tone={projection && projection.kpis_scenario?.final_cash >= projection.kpis_baseline?.final_cash ? "emerald" : "rose"}
          />
          <KpiCard
            testid="scenarios-kpi-lowest-week"
            icon={TrendingDown}
            label={tx(lang, "Lowest week", "Semana más baja", "Semana mais baixa")}
            value={projection?.kpis_scenario?.lowest_week ? `S${projection.kpis_scenario.lowest_week}` : "—"}
            sublabel={projection ? fmtMoney(projection.kpis_scenario?.lowest_cash, currency) : ""}
            tone={projection && (projection.kpis_scenario?.lowest_cash || 0) < 0 ? "rose" : "violet"}
          />
          <KpiCard
            testid="scenarios-kpi-runway"
            icon={Hourglass}
            label={tx(lang, "Runway (months)", "Pista (meses)", "Pista (meses)")}
            value={projection?.kpis_scenario?.runway_months != null ? `${projection.kpis_scenario.runway_months}` : "∞"}
            sublabel={tx(lang, "vs. lowest projected cash", "vs caja más baja", "vs caixa mais baixo")}
            tone="slate"
          />
        </div>

        {/* Chart */}
        <Card className="p-4 rounded-2xl border-slate-200" data-testid="scenarios-chart-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-bold text-slate-900">
              {tx(lang, "13-week cash projection", "Proyección de 13 semanas", "Projeção de 13 semanas")}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadAll()}
              data-testid="scenarios-refresh"
              className="text-xs gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {tx(lang, "Refresh", "Actualizar", "Atualizar")}
            </Button>
          </div>
          <div className="h-72 -ml-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#E7E5DF" strokeDasharray="3 3" />
                <XAxis dataKey="weekLabel" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748B" }}
                  tickFormatter={(v) => {
                    const abs = Math.abs(v);
                    if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
                    if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
                    return v;
                  }}
                />
                <Tooltip
                  formatter={(v) => fmtMoney(v, currency)}
                  contentStyle={{ borderRadius: 12, border: "1px solid #E7E5DF", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="baseline"
                  name={tx(lang, "Baseline", "Sin escenario", "Sem cenário")}
                  stroke="#94A3B8"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="scenario"
                  name={tx(lang, "Scenario", "Escenario", "Cenário")}
                  stroke="#7C3AED"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Mods + presets */}
        <Card className="p-4 rounded-2xl border-slate-200" data-testid="scenarios-mods-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-bold text-slate-900">
              {tx(lang, "What changes?", "¿Qué cambia?", "O que muda?")}
            </h2>
            {(scenario?.mods?.length || 0) > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={reset}
                data-testid="scenarios-reset"
                className="text-xs gap-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              >
                <X className="h-3.5 w-3.5" />
                {tx(lang, "Clear all", "Borrar todo", "Limpar tudo")}
              </Button>
            )}
          </div>

          <p className="text-xs text-slate-500 mb-2">
            {tx(lang, "Pick a preset for your archetype…", "Elige un preset para tu perfil…", "Escolha um preset para seu perfil…")}
          </p>

          {/* iter-210 — One-click stress-test bundle for the archetype */}
          {quickBundle && quickBundle.mods?.length > 0 ? (
            (() => {
              const QuickIcon = Lucide[quickBundle.icon] || Sparkles;
              const title = quickBundle[`title_${lang === "en" ? "en" : lang === "pt" ? "pt" : "es"}`]
                || quickBundle.title_es;
              const why = quickBundle[`why_${lang === "en" ? "en" : lang === "pt" ? "pt" : "es"}`]
                || quickBundle.why_es;
              return (
                <div
                  className="mb-3 rounded-xl border border-sky-200 bg-sky-50 p-3 flex items-start gap-3"
                  data-testid="scenarios-quick-bundle"
                >
                  <div className="h-9 w-9 rounded-lg bg-white border border-sky-200 flex items-center justify-center text-sky-600 flex-shrink-0">
                    <QuickIcon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-900" data-testid="scenarios-quick-title">
                      {title}
                    </div>
                    <div className="text-[11px] text-slate-600 leading-snug mt-0.5" data-testid="scenarios-quick-why">
                      {why}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleQuickScenario}
                    disabled={saving}
                    data-testid="scenarios-quick-apply"
                    className="flex-shrink-0 bg-sky-600 hover:bg-sky-700 text-white text-xs gap-1.5"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {tx(lang, "Apply", "Aplicar", "Aplicar")}
                  </Button>
                </div>
              );
            })()
          ) : null}

          <div className="flex flex-wrap gap-2 mb-4" data-testid="scenarios-presets">
            {presets.map((p, idx) => (
              <PresetChip
                key={`${p.type}-${idx}`}
                preset={p}
                lang={lang}
                onPick={handlePreset}
                disabled={saving}
              />
            ))}
          </div>

          {(scenario?.mods?.length || 0) === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500" data-testid="scenarios-empty">
              {tx(lang,
                "No mods yet — pick a preset above to start.",
                "Sin cambios todavía — elige un preset para empezar.",
                "Sem mudanças ainda — escolha um preset.")}
            </div>
          ) : (
            <div className="space-y-3">
              {scenario.mods.map((m) => (
                <ModEditor
                  key={m.id}
                  mod={m}
                  anchors={anchors}
                  onChange={updateMod}
                  onRemove={removeMod}
                  lang={lang}
                  profileCcy={currency}
                />
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
